/**
 * cart.duck.js
 *
 * A lightweight, client-side shopping basket.
 *
 * Sharetribe's default transaction process purchases a single listing at a
 * time, so there is no server-side "cart" resource. This duck therefore keeps
 * the basket entirely on the client and persists it to `localStorage` so it
 * survives page reloads. Each item stores a small, serialisable snapshot of the
 * listing (title, price, image, author) plus the product variant chosen from it
 * (size, volume, scent) so the CartPage can render without re-fetching every
 * listing.
 *
 * Checkout happens on the CartCheckoutPage, which turns the basket into one
 * transaction per line (Sharetribe's processes buy a single listing at a time)
 * and pays for all of them with one card in one submit.
 */

import { getCartLineId } from '../util/productVariants';
import { getListingEcolabelKeys } from '../config/ecolabels';

const STORAGE_KEY = 'ethy-cart-v1';

/**
 * A basket line is identified by the listing *and* the variant chosen from it,
 * so a small and a medium of the same garment are two lines. Baskets saved
 * before variants existed have no lineId, so the listing id stands in for it.
 *
 * @param {Object} item a basket item (or a checkout line built from one)
 * @returns {string} the line id
 */
export const lineIdOf = item => item?.lineId || item?.listingId;

// ================ Storage helpers ================ //

const canUseStorage = () => typeof window !== 'undefined' && !!window.localStorage;

const loadStoredItems = () => {
  if (!canUseStorage()) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const persistItems = items => {
  if (!canUseStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // Ignore storage write errors (e.g. private mode / quota exceeded).
  }
};

// ================ Snapshot helper ================ //

/**
 * Build a minimal, serialisable cart item from a listing entity.
 *
 * @param {Object} listing listing entity (with attributes + images)
 * @param {number} quantity desired quantity (defaults to 1)
 * @param {Array} [productVariants] the chosen size/volume/scent, as returned by
 *   util/productVariants.js getVariantSelectionEntries
 * @param {number} [maxQuantity] how many of this exact version the brand holds,
 *   so the basket can't be stacked past it. Only a snapshot — the brand may
 *   sell the last one to somebody else while it sits in the basket, which is
 *   why CartCheckoutPage checks again against freshly fetched listings.
 * @returns {Object|null} cart item snapshot or null if the listing is unusable
 */
export const listingToCartItem = (listing, quantity = 1, productVariants = [], maxQuantity) => {
  const id = listing?.id?.uuid;
  if (!id) {
    return null;
  }
  const { title = '', price, publicData } = listing.attributes || {};
  const firstImage = listing.images?.[0];
  const variants = firstImage?.attributes?.variants || {};
  const variant =
    variants['listing-card'] || variants['listing-card-2x'] || variants[Object.keys(variants)[0]];
  const chosenVariants = Array.isArray(productVariants) ? productVariants : [];

  return {
    lineId: getCartLineId(id, chosenVariants),
    listingId: id,
    quantity: Math.max(1, Number.parseInt(quantity, 10) || 1),
    maxQuantity: Number.isInteger(maxQuantity) ? maxQuantity : null,
    // The labels are kept alongside the values so the basket still reads
    // correctly if the brand edits their options afterwards.
    productVariants: chosenVariants,
    title,
    // Price is stored as its raw subunits + currency so it can be rehydrated
    // into a Money instance for formatting on the CartPage.
    price: price ? { amount: price.amount, currency: price.currency } : null,
    authorName: listing.author?.attributes?.profile?.displayName || null,
    imageURL: variant?.url || null,
    listingType: publicData?.listingType || null,
    // The ecolabel keys, for the basket's "Ecolabels in your basket" summary.
    ecolabels: getListingEcolabelKeys(publicData),
  };
};

// ================ Action types ================ //

export const HYDRATE_CART = 'app/cart/HYDRATE_CART';
export const ADD_TO_CART = 'app/cart/ADD_TO_CART';
export const UPDATE_CART_QUANTITY = 'app/cart/UPDATE_CART_QUANTITY';
export const REMOVE_FROM_CART = 'app/cart/REMOVE_FROM_CART';
export const CLEAR_CART = 'app/cart/CLEAR_CART';
export const RESTORE_CART_ITEM = 'app/cart/RESTORE_CART_ITEM';
export const CLOSE_MINI_CART = 'app/cart/CLOSE_MINI_CART';

// ================ Reducer ================ //

// NOTE: initialState is deterministic (empty) so server and client render match.
// The persisted basket is merged in on the client via hydrateCart().
const initialState = {
  items: [],
  // False until the stored basket has been read on the client. Until then an
  // empty `items` means "not loaded yet", not "the basket is empty".
  hydrated: false,
  // The mini-basket drawer opens after each add, showing what was just added.
  miniCartOpen: false,
  lastAddedLineId: null,
};

/**
 * Never let a line ask for more than the brand has of that exact version.
 *
 * @param {number} quantity the quantity being asked for
 * @param {number|null} [maxQuantity] the snapshot count, or null when the
 *   listing doesn't track stock per variation
 * @returns {number} the quantity to store
 */
const capQuantity = (quantity, maxQuantity) =>
  Number.isInteger(maxQuantity) ? Math.min(quantity, maxQuantity) : quantity;

const mergeItem = (items, newItem) => {
  const existingIndex = items.findIndex(i => lineIdOf(i) === lineIdOf(newItem));
  if (existingIndex === -1) {
    return [...items, { ...newItem, quantity: capQuantity(newItem.quantity, newItem.maxQuantity) }];
  }
  return items.map((item, index) =>
    index === existingIndex
      ? {
          ...item,
          ...newItem,
          // Tapping "add" on a line already in the basket tops it up, but only
          // as far as the brand can actually supply.
          quantity: capQuantity(item.quantity + newItem.quantity, newItem.maxQuantity),
        }
      : item
  );
};

export default function cartReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case HYDRATE_CART:
      return { ...state, items: Array.isArray(payload) ? payload : [], hydrated: true };
    case ADD_TO_CART:
      return {
        ...state,
        items: mergeItem(state.items, payload),
        miniCartOpen: true,
        lastAddedLineId: lineIdOf(payload),
      };
    case RESTORE_CART_ITEM: {
      // Undo of a removal: the line goes back where it was.
      const { item, index } = payload;
      if (state.items.some(i => lineIdOf(i) === lineIdOf(item))) {
        return state;
      }
      const items = [...state.items];
      items.splice(Math.min(Math.max(index, 0), items.length), 0, item);
      return { ...state, items };
    }
    case CLOSE_MINI_CART:
      return { ...state, miniCartOpen: false };
    case UPDATE_CART_QUANTITY: {
      const { lineId, quantity } = payload;
      if (quantity <= 0) {
        return { ...state, items: state.items.filter(i => lineIdOf(i) !== lineId) };
      }
      return {
        ...state,
        items: state.items.map(i =>
          lineIdOf(i) === lineId ? { ...i, quantity: capQuantity(quantity, i.maxQuantity) } : i
        ),
      };
    }
    case REMOVE_FROM_CART:
      return { ...state, items: state.items.filter(i => lineIdOf(i) !== payload) };
    case CLEAR_CART:
      return { ...state, items: [] };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const hydrateCartAction = items => ({ type: HYDRATE_CART, payload: items });
export const addToCartAction = item => ({ type: ADD_TO_CART, payload: item });
export const updateCartQuantityAction = (lineId, quantity) => ({
  type: UPDATE_CART_QUANTITY,
  payload: { lineId, quantity },
});
export const removeFromCartAction = lineId => ({ type: REMOVE_FROM_CART, payload: lineId });
export const clearCartAction = () => ({ type: CLEAR_CART });
export const restoreCartItemAction = (item, index) => ({
  type: RESTORE_CART_ITEM,
  payload: { item, index },
});
export const closeMiniCart = () => ({ type: CLOSE_MINI_CART });

// ================ Thunks ================ //
// Each thunk persists the resulting basket to localStorage.

export const hydrateCart = () => dispatch => {
  dispatch(hydrateCartAction(loadStoredItems()));
};

export const addToCart = (listing, quantity = 1, productVariants = [], maxQuantity) => (
  dispatch,
  getState
) => {
  const item = listingToCartItem(listing, quantity, productVariants, maxQuantity);
  if (!item) {
    return false;
  }
  dispatch(addToCartAction(item));
  persistItems(getState().cart.items);
  return true;
};

/**
 * Put a removed line back, at the position it was removed from.
 *
 * @param {Object} item the removed basket item
 * @param {number} index where it was in the basket
 */
export const restoreCartItem = (item, index) => (dispatch, getState) => {
  dispatch(restoreCartItemAction(item, index));
  persistItems(getState().cart.items);
};

export const updateCartQuantity = (lineId, quantity) => (dispatch, getState) => {
  dispatch(updateCartQuantityAction(lineId, quantity));
  persistItems(getState().cart.items);
};

export const removeFromCart = lineId => (dispatch, getState) => {
  dispatch(removeFromCartAction(lineId));
  persistItems(getState().cart.items);
};

export const clearCart = () => (dispatch, getState) => {
  dispatch(clearCartAction());
  persistItems(getState().cart.items);
};

// ================ Selectors ================ //

export const selectCartItems = state => state.cart.items;

export const selectIsCartHydrated = state => !!state.cart.hydrated;

export const selectCartCount = state =>
  state.cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
